import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin, Instagram, Youtube, ExternalLink } from 'lucide-react';
import Logo from '../components/Logo';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const location = useLocation();

  const handleExternalClick = (e, url) => {
    e.preventDefault();
    setExternalUrl(url);
    setShowModal(true);
  };

  const confirmRedirect = () => {
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
    setShowModal(false);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Início', href: '/' },
    { name: 'Quem Somos', href: '/sobre' },
    { name: 'Palavras', href: '/palavras' },
    { name: 'Dízimos e Ofertas', href: '/dizimos' },
    { name: 'Ministérios', href: '/ministerios' },
    { name: 'Agenda', href: '/agenda' },
    { name: 'MEVAM Social', href: 'https://www.mevamsocial.org.br/', external: true },
    { name: 'Contato', href: '/contato' },
  ];

  // Sempre volta ao topo quando a rota muda
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleNavClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#030B2E]">
      {/* Header */}
  <header className="fixed w-full z-50 transition-all duration-300" style={{ backgroundColor: '#030B2E' }}>
        {/* Top Bar */}
  <div className="py-2 px-4 hidden md:block" style={{ backgroundColor: '#030B2E', color: '#fff' }}>
          <div className="container-custom flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Phone size={16} />
                <span>(61) 99818-7484</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} />
                <span>contato@mevambrasilia.org</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin size={16} />
                <span>Brasília - DF</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
            
              <a href="https://www.instagram.com/mevam.brasilia/" target="_blank" rel="noopener noreferrer" className="hover:text-[#17E6B3] transition-colors">
                <Instagram size={18} />
              </a>
              <a href="https://www.youtube.com/@mevam.brasilia" target="_blank" rel="noopener noreferrer" className="hover:text-[#17E6B3] transition-colors">
                <Youtube size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
          <nav className="container-custom py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <Logo isScrolled={isScrolled} size="md" />
              </Link>

              {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                item.external ? (
                  <button
                    key={item.name}
                    onClick={(e) => handleExternalClick(e, item.href)}
                    className="font-medium transition-colors relative text-white hover:text-[#17E6B3]"
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={`font-medium transition-colors relative ${location.pathname === item.href ? 'text-[#17E6B3]' : 'text-white hover:text-[#17E6B3]'}`}
                  >
                    {item.name}
                    {location.pathname === item.href && (
                      <motion.div
                        className="absolute bottom-[-4px] left-0 right-0 h-0.5 bg-[#17E6B3]"
                        layoutId="activeNavItem"
                      />
                    )}
                  </Link>
                )
              ))}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden transition-colors text-white hover:text-[#17E6B3]"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-white border-t shadow-lg"
          >
            <div className="container-custom py-4">
              {navigation.map((item) => (
                item.external ? (
                  <button
                    key={item.name}
                    onClick={(e) => handleExternalClick(e, item.href)}
                    className="block py-3 font-medium text-[#030B2E] hover:text-[#17E6B3] transition-colors w-full text-left"
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block py-3 font-medium transition-colors ${
                      location.pathname === item.href
                        ? 'text-[#17E6B3]'
                        : 'text-[#030B2E] hover:text-[#17E6B3]'
                    }`}
                    onClick={handleNavClick}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </motion.div>
        )}
      </header>

      {/* Modal de Redirecionamento */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
            >
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#030B2E] to-[#17E6B3] rounded-full flex items-center justify-center mb-6">
                  <ExternalLink className="text-white" size={32} />
                </div>
                
                <h3 className="text-2xl font-bold text-[#030B2E] mb-3">
                  Redirecionamento Externo
                </h3>
                
                <p className="text-[#B8C4E3] mb-6">
                  Você será redirecionado para o site do <strong>MEVAM Social</strong> em uma nova aba.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-[#17E6B3]/20">
                  <p className="text-sm text-[#B8C4E3] mb-1">Destino:</p>
                  <p className="text-[#030B2E] font-mono text-sm break-all">
                    {externalUrl}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-[#B8C4E3] text-[#030B2E] font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmRedirect}
                    className="flex-1 px-6 py-3 bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold rounded-lg hover:shadow-lg transition-all"
                  >
                    Continuar
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-24">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#030B2E] text-white">
        <div className="container-custom section-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Logo e Descrição */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <Logo size="md" />
              </div>
              <p className="text-[#B8C4E3] mb-6 max-w-md">
                Uma igreja que ama, serve e edifica vidas. Fazemos parte do MEVAM Global, 
                compartilhando os mesmos princípios e valores de um ministério de alcance mundial.
              </p>
              <div className="flex space-x-4">
              
                <a href="https://www.instagram.com/mevam.brasilia/" target="_blank" rel="
                opener noreferrer" className="hover:text-[#17E6B3] transition-colors">
                  <Instagram size={24} />
                </a>
                <a href="https://www.youtube.com/@mevam.brasilia" target="_blank" rel="noopener noreferrer" className="hover:text-[#17E6B3] transition-colors">
                  <Youtube size={24} />
                </a>
              </div>
            </div>

            {/* Links Rápidos */}
            <div>
              <h4 className="text-lg font-semibold mb-6 text-[#17E6B3]">Links Rápidos</h4>
              <ul className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    {item.external ? (
                      <button
                        onClick={(e) => handleExternalClick(e, item.href)}
                        className="text-[#B8C4E3] hover:text-white transition-colors text-left"
                      >
                        {item.name}
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={handleNavClick}
                        className="text-[#B8C4E3] hover:text-white transition-colors"
                      >
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contato */}
            <div>
              <h4 className="text-lg font-semibold mb-6 text-[#17E6B3]">Contato</h4>
              <ul className="space-y-3 text-[#B8C4E3]">
                <li className="flex items-center space-x-3">
                  <Phone size={16} />
                  <span>(61) 99818-7484 </span>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail size={16} />
                  <span>contato@mevambrasilia.org</span>
                </li>
                <li className="flex items-center space-x-3">
                  <MapPin size={16} />
                  <span>Brasília - DF</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#17E6B3]/20">
          <div className="container-custom py-6">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-[#B8C4E3]">
              <p>&copy; 2025 MEVAM Brasília Ceilândia-DF. Todos os direitos reservados.</p>
              <p>Desenvolvido com ❤️ para o Reino de Deus</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;